import { useLoaderData } from "react-router";

import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { CartItemType, LeadTimePriceType, ProductType, UserType } from "../../types";
import { useAppState } from "../../context";
import LeadTimePriceInput from "../../ui/lead-time-price-input.tsx";
import { AmountInput, ProdInfo } from "../../ui";
import { PRODUCT_TYPE } from "../../constants";
import useCart from "../cart/useCart.ts";
import useWishList from "../wishlist/useWishList.ts";
import PopularVideoStories from "../../blocks/pupular-video-stories/index.tsx";
import CategoryVideoStories from "../../blocks/more-vide-stories/index.tsx";

const CardPage: React.FC = () => {
    const product = useLoaderData() as ProductType;
    const { name, price, preview, clipProperties, images } = product;

    const [sliderActive, setSliderActive] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const swiperRef = useRef<SwiperRef | null>(null);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const { addToWishlist, isInWishlist, removeFromWishlist } = useWishList();
    const { updateCartItem, loading: isCartLoading, removeFromCart, findCartItem } = useCart();
    const [leadTimePrices] = useAppState<LeadTimePriceType[]>('leadTimePrices', []);

    const defaultCartItem = {
        product: product.id,
        additionalPhotoCount: 0,
        id: undefined,
        leadTimePrice: leadTimePrices.find(p => p.price === 0)?.id || undefined,
    } as CartItemType;

    const [cartItem, setCartItem] = useState<CartItemType>(findCartItem(product) || defaultCartItem);
    const [videoError, setVideoError] = useState<string | null>(null);

    // Update video source when product changes
    useEffect(() => {
        const video = videoRef.current;
        if (video && preview) {
            // Reset states
            setVideoPlaying(false);
            setVideoError(null);
            video.pause();
        }
    }, [preview]);

    useEffect(() => {
        if (cartItem.id) {
            updateCartItem(cartItem)
        }
    }, [cartItem])

    // Handle video events
    useEffect(() => {
        const video = videoRef.current;

        const handlePlay = () => setVideoPlaying(true);
        const handlePause = () => setVideoPlaying(false);
        const handleEnded = () => setVideoPlaying(false);
        const handleError = () => {
            if (video) {
                console.error(video.error, video.src);
            }
            setVideoError('Failed to load video');
            setVideoPlaying(false);
        };

        if (video) {
            video.addEventListener('play', handlePlay);
            video.addEventListener('pause', handlePause);
            video.addEventListener('ended', handleEnded);
            video.addEventListener('error', handleError);
        }

        return () => {
            if (video) {
                video.removeEventListener('play', handlePlay);
                video.removeEventListener('pause', handlePause);
                video.removeEventListener('ended', handleEnded);
                video.removeEventListener('error', handleError);
            }
        }
    }, [videoRef]); // Only run once on mount

    // Cleanup on unmount or product change
    useEffect(() => {
        return () => {
            const video = videoRef.current;
            if (video) {
                video.pause();
                video.removeAttribute('src'); // More thorough than setting empty string
                video.load();
                if (video.readyState >= 2) { // If video has loaded enough data
                    try {
                        video.currentTime = 0;
                    } catch (e) {
                        console.warn('Could not reset video time:', e);
                    }
                }
            }
            setSliderActive(false);
            setVideoPlaying(false);

        }
    }, [product, videoRef]);

    const playVideo = async () => {
        if (videoRef.current) {
            try {
                // iOS requires user interaction and often needs muted videos
                videoRef.current.muted = true;
                await videoRef.current.play();
                // Try to unmute after successful play
                videoRef.current.muted = false;
            } catch (error) {
                console.error('Video playback error:', error);
                setVideoError('Failed to play video');
            }
        }
    }

    const pauseVideo = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
    }

    return (
        <>
            <section className="section product">
                <div className="product__inner inside-wrapper">
                    <div className={`product__pics ${sliderActive ? ' m-active' : ''}`}>
                        {/* Picture buttons */}
                        <button
                            onClick={() => {
                                if (swiperRef.current) {
                                    swiperRef.current.swiper.slideToLoop(0, 0);
                                }
                                setSliderActive(true)
                                playVideo();
                            }}

                            className={`product__picholder product__picholder_main`}

                        >
                            <picture className="product__pic">
                                <source type="image/webp"
                                    srcSet={`/api/media/${images[0].image}?webp&s=s 1x, /api/media/${images[0].image}?webp&s=m 2x`}
                                />
                                <img
                                    className="product__pic-img"
                                    src={`/api/media/${images[0].image}?s=s`}
                                    srcSet={`/api/media/${images[0].image}?s=m 2x`}
                                    alt="Watch picture"
                                />
                            </picture>
                            <span className="btn-play"></span>
                        </button>
                        {images.slice(1).map(({ image }, i) => <button
                            onClick={() => {
                                if (swiperRef.current) {
                                    swiperRef.current.swiper.slideToLoop(i + 1, 0);
                                }
                                setSliderActive(true)
                                pauseVideo();
                            }}
                            key={i}
                            className={`product__picholder`}

                        >
                            <picture className="product__pic">
                                <source type="image/webp" srcSet={`/api/media/${image}?webp&s=s 1x, /api/media/${image}?webp&s=m 2x`} />
                                <img
                                    className="product__pic-img"
                                    src={`/api/media/${image}?s=s`}
                                    srcSet={`/api/media/${image}?s=m 2x`}
                                    alt="Watch picture"
                                />
                            </picture>

                        </button>
                        )}

                        {/* Slider */}
                        <div className="product__slider-holder">
                            <button
                                className="product__slider-overlay"
                                type="button"
                            ></button>
                            <Swiper className="product__slider swiper js_swiper_product"
                                ref={swiperRef}
                                initialSlide={0}
                                modules={[Navigation]}
                                navigation={{
                                    nextEl: '.slider__controls .slider__arrow_next',
                                    prevEl: '.slider__controls .slider__arrow_prev'
                                }}
                                onSlideChange={(swiper) => {
                                    if (swiper.realIndex !== 0) {
                                        pauseVideo()
                                    }
                                }}
                            >
                                <SwiperSlide className="swiper-slide">
                                    <div className={`product__fullvideo video ${videoPlaying ? 'm-active' : ''}`}>
                                        <video
                                            controls={true}
                                            className="video__video"
                                            preload="auto"
                                            playsInline
                                            webkit-playsinline="true"
                                            poster={`/api/media/${images[0].image}?s=m`}
                                            ref={videoRef}
                                        >
                                            <source src={`/api/media/${preview}`} type="video/mp4" />
                                        </video>
                                        <button
                                            className="btn-play"
                                            onClick={playVideo}
                                            type="button"
                                            aria-label="Play"
                                        ></button>
                                    </div>
                                </SwiperSlide>
                                {images.slice(1).map(({ image }, i) => (
                                    <SwiperSlide key={i + 1} className="swiper-slide">
                                        <picture className="product__fullpic">
                                            <source type="image/webp" srcSet={`/api/media/${image}?webp&s=m 1x, /api/media/${image}?webp&s=l 2x`} />
                                            <img
                                                className="product__fullpic-img"
                                                src={`/api/media/${image}?s=m`}
                                                srcSet={`/api/media/${image}?s=l 2x`}
                                                alt=""
                                                loading="lazy"
                                            />
                                        </picture>
                                    </SwiperSlide>
                                ))}

                                <div className="slider__controls">
                                    <div className="slider__arrow slider__arrow_prev"></div>
                                    <div className="slider__arrow slider__arrow_next"></div>
                                </div>

                                <button
                                    className="btn-media-close product__slider-close js_prodslider_toggle"
                                    type="button"
                                    onClick={() => {
                                        pauseVideo();
                                        setSliderActive(false)
                                    }}
                                >
                                    <svg role="img" className="btn-media-close__ico">
                                        <use href="./img/sprite.svg#cross"></use>
                                    </svg>
                                </button>
                            </Swiper>
                        </div>
                    </div>

                    <div className="product__controls">
                        <div className="product__head">
                            <h1 className="product__name">{name}</h1>
                            <div className="product__price-row">
                                <span className="product__price">${price}</span>

                                {clipProperties && <ProdInfo {...clipProperties} />}
                            </div>
                        </div>

                        <div className="product__btns">

                            {!isCartLoading && (
                                (() => {
                                    if (!cartItem || !cartItem.id) {
                                        return <button className="product__cart-btn btn btn_md btn_cart" onClick={() => {
                                            updateCartItem(cartItem, setCartItem)
                                        }} disabled={!product.isAvailable}>
                                            <span className="btn__cart-text">
                                                <span className="btn__cart-text-inactive">ADD TO CART</span>
                                                <span className="btn__cart-text-active">IN THE CART</span>
                                            </span>
                                            <svg role="img" className="btn__ico">
                                                <use href="./img/sprite.svg#cart"></use>
                                            </svg>
                                        </button>
                                    }
                                    return <button
                                        disabled={!product.isAvailable}
                                        onClick={() => {
                                            removeFromCart(cartItem)
                                            setCartItem(defaultCartItem);
                                        }}
                                        className="product__cart-btn btn btn_md btn_cart btn_cart-active">
                                        <span className="btn__cart-text">
                                            <span className="btn__cart-text-inactive">ADD TO CART</span>
                                            <span className="btn__cart-text-active">IN THE CART</span>
                                        </span>
                                        <svg role="img" className="btn__ico">
                                            <use href="./img/sprite.svg#cart"></use>
                                        </svg>
                                    </button>
                                })())
                            }
                            <button
                                onClick={() => {
                                    isInWishlist(product) ? removeFromWishlist(product) : addToWishlist(product)
                                }}
                                className={`product__fav-btn btn btn_md btn_wishlist ${isInWishlist(product) ? 'btn_wishlist-active' : ''}`}
                                type="button"
                                title={`${isInWishlist(product) ? 'Remove from' : 'Add to'} Wishlist`}
                            >
                                <svg role="img" className="btn__ico">
                                    <use href="./img/sprite.svg#heart"></use>
                                </svg>
                            </button>
                        </div>

                        {/* Active buttons */}


                        {
                            product.type === PRODUCT_TYPE.CLIP &&
                            product.clipProperties &&
                            <div className="product__extra">
                                <h2 className="product__extra-title">Add Something Extra</h2>
                                <div className="product__extra-row">
                                    <div className="product__extra-subtitle">
                                        If&nbsp;you want to&nbsp;add more photos to&nbsp;this&nbsp;video&nbsp;story
                                    </div>
                                    <div className="product__extra-col">
                                        <AmountInput initialValue={cartItem?.additionalPhotoCount || 0}
                                            onChange={(val) => {
                                                  setCartItem(prev => ({
                                                    ...prev,
                                                    additionalPhotoCount: val 
                                                }))
                                            }}
                                        />
                                    </div>

                                </div>

                                {
                                    leadTimePrices.length > 1 && <div className="product__extra-row">
                                        <div className="product__extra-subtitle">
                                            If&nbsp;you want to&nbsp;get a&nbsp;video story faster
                                        </div>

                                        <LeadTimePriceInput uiVariant="product" initialValue={cartItem?.leadTimePrice}
                                            onChange={(value) => {
                                                setCartItem(prev => ({
                                                    ...prev,
                                                    leadTimePrice: value.id
                                                }))
                                            }} />

                                    </div>
                                }

                                {/*<div className="product__extra-row">
                                    <div className="product__extra-subtitle">
                                        If&nbsp;you want to&nbsp;give a&nbsp;video story as&nbsp;a&nbsp;beautiful&nbsp;gift
                                    </div>
                                    <div className="product__extra-col">
                                        <picture className="product__player-pic">
                                            <source
                                                type="image/webp"

                                                srcSet="./img/vcard.webp"
                                            />
                                            <img
                                                className="product__player-pic-img"
                                                src="./img/vcard.jpg"
                                                srcSet="./img/vcard.jpg 2x"
                                                alt=""
                                                loading="lazy"
                                            />
                                        </picture>
                                    </div>
                                    <div className="product__extra-col">
                                        <label className="btnradiocheck">
                                            <input type="checkbox"
                                                className="btnradiocheck__input"
                                                defaultChecked={cartItem.giftVideoStory || false}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    setCartItem(prev => ({
                                                        ...prev,
                                                        giftVideoStory: event.currentTarget.checked
                                                    }))
                                                }}
                                            />
                                            <span className="btnradiocheck__label">Video card player</span>
                                            <span className="btnradiocheck__btn">$50</span>
                                        </label>
                                    </div>
                                </div>
                                */}
                            </div>
                        }
                    </div>
                </div>
            </section>
            <CategoryVideoStories categories={product.categories} exclude={[product]} />
            <PopularVideoStories exclude={[product.id]} categories={product.categories} />
        </>
    );
};


export default CardPage;